import { createEvent, createStore } from 'effector';

// Модалка может быть либо легаси-простой ('share' / 'example'), либо drill-down
// со списком элементов (источник → его URL'ы, тип факта → факты, и т.п.).
// Раньше это был enum ModalKind = 'share' | 'example' | null — каждый клик по
// карточке Footprint просто звал toast, потому что drill-down было некуда
// положить. Discriminated union даёт generic-контент без захардкоживания
// каждого нового сценария.
export type ModalDrillItem = {
  id: string;
  // Основная подпись (URL, заголовок, значение факта).
  primary: string;
  // Подзаголовок под primary (источник, тип, snippet) — необяз.
  secondary?: string;
  // Колонка справа (kind: accepted/uncertain, confidence, count).
  meta?: string;
  // Если задан — строка превращается в внешнюю ссылку.
  href?: string;
};

export type ModalDrillPayload = {
  title: string;
  // Маленький подзаголовок в шапке (например «фильтр: WHOIS»).
  subtitle?: string;
  items: ModalDrillItem[];
  // Включает поле поиска по primary/secondary/meta.
  searchable?: boolean;
  // Размер страницы пагинации; если не задан — без пагинации.
  pageSize?: number;
  // Сообщение, когда items.length === 0.
  emptyMessage?: string;
};

// Подтверждение запуска поиска. Шаг между «Собрать отчёт» и реальным
// startSearchFx — пользователь видит сводку введённых данных, читает
// ещё раз, и только через 3 сек кнопка «Запустить» становится активной
// (защита от случайного клика, плюс время на проверку identifier'ов).
// Cancel-кнопки нет (workers всё равно её не уважают), поэтому проверка
// данных до запуска — последний шанс поправить.
export type ModalConfirmSearchPayload = {
  // Уже отнормализованные пары (label, value) — Onboard формирует их под
  // свои стили, чтобы модалка не лезла в форм-state и осталась generic.
  rows: { label: string; value: string }[];
  // Колбэк, который вызывается по нажатию «Запустить» после 3-сек гарда.
  onConfirm: () => void;
};

export type ModalState =
  | { kind: 'share' }
  | { kind: 'example' }
  | { kind: 'drill-down'; payload: ModalDrillPayload }
  | { kind: 'confirm-search'; payload: ModalConfirmSearchPayload }
  | null;

export type ModalKind = NonNullable<ModalState>['kind'];

export const modalOpened = createEvent<Exclude<ModalState, null>>();
export const drillDownOpened = createEvent<ModalDrillPayload>();
export const confirmSearchOpened = createEvent<ModalConfirmSearchPayload>();
export const modalClosed = createEvent();

export const $modal = createStore<ModalState>(null)
  .on(modalOpened, (_, m) => m)
  .on(drillDownOpened, (_, payload) => ({ kind: 'drill-down' as const, payload }))
  .on(confirmSearchOpened, (_, payload) => ({ kind: 'confirm-search' as const, payload }))
  .reset(modalClosed);
